import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { CartItem } from '@/pages/home/page';
import HomeSearchBar from '@/pages/home/components/HomeSearchBar';
import { supabase } from '@/lib/supabase';
import CollectionGrid, { type LayoutStyle } from '@/pages/home/components/CollectionGrid';
import { useTheme } from '@/hooks/useTheme';
import {
  CarouselBanner,
  Card3DBanner,
  GlassBanner,
  NeonGlowBanner,
  BigSplit3DBanner,
  MagazineGridBanner,
  CinematicBanner,
  ThemeShowcaseBanner,
  AnnouncementBanner,
  MinimalLuxuryBanner,
} from '@/pages/home/components/BannerStyles';

interface TrendingProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  thumbnail: string;
  images: string[];
  sizes: string[];
}

interface HeroProps {
  onShopNowClick?: () => void;
  cart: CartItem[];
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
  onOpenCart: () => void;
}

interface Product {
  id: number | string;
  name: string;
  price: number;
  originalPrice: number;
  image: string;
  category: string;
  badge: string;
}

const categories = ['Track Pants', 'T-Shirts', 'Jeans', 'Hoodies', 'Shorts'];

const categoryIcons: Record<string, string> = {
  'Track Pants': 'ri-boxing-line',
  'T-Shirts': 'ri-t-shirt-line',
  'Jeans': 'ri-scissors-line',
  'Hoodies': 'ri-cloud-windy-line',
  'Shorts': 'ri-riding-line',
};

const fallbackProducts: Product[] = [
  {
    id: 101, name: 'Baggy Black Street', price: 699, originalPrice: 999,
    category: 'Track Pants', badge: 'BESTSELLER',
    image: 'https://storage.readdy-site.link/project_files/2234b9f6-96c3-4b2e-b8bc-dbceabbc0a51/5f70227a-e8f5-4213-8712-4064f2fd83be_Screenshot-2026-04-01-at-5.30.01PM.png?v=377fd675b7aa6884310792b624de6f13',
  },
  {
    id: 102, name: 'Navy Wide Leg', price: 749, originalPrice: 1099,
    category: 'Track Pants', badge: 'TOP SELLING',
    image: 'https://storage.readdy-site.link/project_files/2234b9f6-96c3-4b2e-b8bc-dbceabbc0a51/444bddb6-d2a8-462b-bc38-9c5ededa40d3_Screenshot-2026-04-01-at-5.29.52PM.png?v=7dedf2de1ed261287b534f868c21a79d',
  },
  {
    id: 103, name: 'Grey Drip Baggy', price: 799, originalPrice: 1199,
    category: 'Track Pants', badge: 'TRENDING',
    image: 'https://storage.readdy-site.link/project_files/2234b9f6-96c3-4b2e-b8bc-dbceabbc0a51/07236507-e048-406c-ae76-3ef0c533df86_Screenshot-2026-04-01-at-5.30.09PM.png?v=dcac69127ab0e00bfa00da95a69480b0',
  },
  {
    id: 104, name: 'Olive Cargo Baggy', price: 849, originalPrice: 1299,
    category: 'Track Pants', badge: 'NEW',
    image: 'https://storage.readdy-site.link/project_files/2234b9f6-96c3-4b2e-b8bc-dbceabbc0a51/e3a72f97-ddd4-4058-977a-ed95c69eaa0f_Screenshot-2026-04-01-at-5.38.18PM.png?v=f6f89576591ae61fe88f63362aeb476c',
  },
  {
    id: 201, name: 'Classic White Tee', price: 449, originalPrice: 699,
    category: 'T-Shirts', badge: 'BESTSELLER',
    image: 'https://readdy.ai/api/search-image?query=Premium%20white%20crew%20neck%20t-shirt%20flat%20lay%20product%20photography%2C%20clean%20minimal%20cotton%20fabric%20texture%2C%20soft%20studio%20lighting%20on%20light%20grey%20gradient%20background%2C%20high-end%20fashion%20e-commerce%20photography%2C%20crisp%20sharp%20details%2C%20luxury%20casualwear%20aesthetic&width=800&height=1360&seq=201&orientation=portrait',
  },
  {
    id: 202, name: 'Graphic Drop Tee', price: 549, originalPrice: 849,
    category: 'T-Shirts', badge: 'TRENDING',
    image: 'https://readdy.ai/api/search-image?query=Premium%20oversized%20black%20graphic%20print%20t-shirt%20flat%20lay%20product%20photography%2C%20bold%20minimalist%20chest%20print%20design%2C%20heavyweight%20cotton%20fabric%20texture%2C%20professional%20studio%20lighting%20on%20dark%20charcoal%20gradient%20background%2C%20high-end%20streetwear%20e-commerce%20style%2C%20urban%20luxury%20aesthetic&width=800&height=1360&seq=202&orientation=portrait',
  },
  {
    id: 301, name: 'Slim Fit Dark', price: 1299, originalPrice: 1899,
    category: 'Jeans', badge: 'HOT',
    image: 'https://readdy.ai/api/search-image?query=Premium%20slim%20fit%20dark%20indigo%20wash%20denim%20jeans%20neatly%20folded%20product%20photography%2C%20clean%20modern%20denim%20design%20with%20sharp%20stitching%20details%2C%20professional%20studio%20lighting%20on%20soft%20neutral%20background%2C%20high-end%20fashion%20e-commerce%20style%2C%20luxury%20denim%20aesthetic&width=800&height=1360&seq=301&orientation=portrait',
  },
  {
    id: 401, name: 'Oversized Grey', price: 999, originalPrice: 1499,
    category: 'Hoodies', badge: 'POPULAR',
    image: 'https://readdy.ai/api/search-image?query=Premium%20oversized%20grey%20pullover%20hoodie%20flat%20lay%20product%20photography%2C%20thick%20fleece%20kangaroo%20pocket%20design%2C%20soft%20cozy%20fabric%20texture%20visible%2C%20professional%20studio%20lighting%20on%20clean%20light%20background%2C%20high-end%20streetwear%20e-commerce%20photography%2C%20luxury%20cozy%20urban%20aesthetic&width=800&height=1360&seq=401&orientation=portrait',
  },
  {
    id: 501, name: 'Sport Flex Short', price: 449, originalPrice: 649,
    category: 'Shorts', badge: 'BESTSELLER',
    image: 'https://readdy.ai/api/search-image?query=Premium%20navy%20blue%20athletic%20sport%20shorts%20product%20photography%20flat%20lay%2C%20lightweight%20moisture-wicking%20fabric%20with%20white%20side%20stripe%2C%20professional%20studio%20lighting%20on%20clean%20white%20surface%2C%20high-end%20activewear%20e-commerce%20photography%20style%2C%20luxury%20sportswear%20aesthetic&width=800&height=1360&seq=501&orientation=portrait',
  },
  {
    id: 502, name: 'Cargo Khaki Short', price: 549, originalPrice: 799,
    category: 'Shorts', badge: 'TRENDING',
    image: 'https://readdy.ai/api/search-image?query=Premium%20khaki%20beige%20cargo%20shorts%20flat%20lay%20product%20photography%2C%20multiple%20functional%20side%20pockets%2C%20relaxed%20fit%20cotton%20twill%20fabric%2C%20professional%20studio%20lighting%20on%20warm%20sand%20gradient%20background%2C%20high-end%20casual%20e-commerce%20photography%2C%20outdoor%20luxury%20aesthetic&width=800&height=1360&seq=502&orientation=portrait',
  },
  {
    id: 302, name: 'Straight Light Wash', price: 1199, originalPrice: 1699,
    category: 'Jeans', badge: 'NEW',
    image: 'https://readdy.ai/api/search-image?query=Premium%20straight%20leg%20light%20blue%20faded%20wash%20jeans%20folded%20product%20photography%2C%20relaxed%20modern%20denim%20with%20subtle%20distressing%2C%20professional%20studio%20lighting%20on%20warm%20cream%20gradient%20background%2C%20high-end%20fashion%20e-commerce%20style%2C%20casual%20luxury%20denim%20aesthetic&width=800&height=1360&seq=302&orientation=portrait',
  },
  {
    id: 402, name: 'Zip-Up Black', price: 1099, originalPrice: 1599,
    category: 'Hoodies', badge: 'NEW',
    image: 'https://readdy.ai/api/search-image?query=Premium%20black%20full%20zip-up%20hoodie%20product%20photography%20flat%20lay%2C%20sleek%20minimal%20design%20with%20silver%20zipper%20detail%2C%20thick%20cotton%20blend%20fabric%2C%20professional%20studio%20lighting%20on%20dark%20background%2C%20high-end%20streetwear%20e-commerce%20style%2C%20luxury%20urban%20aesthetic%2C%20front%20view&width=800&height=1360&seq=402&orientation=portrait',
  },
];

type BannerLayout =
  | 'carousel'
  | 'card-3d'
  | 'glass-hero'
  | 'neon-glow'
  | 'big-split-3d'
  | 'magazine-grid'
  | 'cinematic'
  | 'theme-showcase'
  | 'announcement'
  | 'minimal-luxury';

export default function Hero({ onShopNowClick, onAddToCart }: HeroProps) {
  const { colors } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [activeBanner, setActiveBanner] = useState(0);
  const [trendingProducts, setTrendingProducts] = useState<TrendingProduct[]>([]);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showSizeModal, setShowSizeModal] = useState(false);
  const bannerTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [activeCategory, setActiveCategory] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [heroBanners, setHeroBanners] = useState<Product[]>(fallbackProducts);
  const [collectionLayout, setCollectionLayout] = useState<LayoutStyle>('grid2x2');
  const [bannerLayout, setBannerLayout] = useState<BannerLayout>('carousel');
  const [countdown, setCountdown] = useState({ hours: 23, minutes: 59, seconds: 59 });

  // Load banner layout from Supabase
  useEffect(() => {
    const loadBannerLayout = async () => {
      try {
        const { data, error } = await supabase.from('banner_layouts').select('layout_style').eq('id', 'main').maybeSingle();
        if (error) {
          console.error('Banner layout load error:', error);
          return;
        }
        if (data?.layout_style) {
          console.log('Banner layout loaded:', data.layout_style);
          setBannerLayout(data.layout_style as BannerLayout);
        }
      } catch (err) {
        console.error('Banner layout exception:', err);
      }
    };
    loadBannerLayout();
    
    // Real-time subscription with proper config
    const sub = supabase
      .channel('banner-layouts-home')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'banner_layouts', filter: 'id=eq.main' },
        (payload) => {
          console.log('Banner layout changed:', payload);
          const newLayout = payload.new as { layout_style?: string };
          if (newLayout?.layout_style) {
            setBannerLayout(newLayout.layout_style as BannerLayout);
          }
        }
      )
      .subscribe((status) => {
        console.log('Banner layout subscription status:', status);
      });
      
    return () => {
      supabase.removeChannel(sub);
    };
  }, []);

  // Load collection layout setting from Supabase
  useEffect(() => {
    const loadLayout = async () => {
      try {
        const { data } = await supabase.from('collection_settings').select('layout_style').eq('id', 'main').maybeSingle();
        if (data?.layout_style) setCollectionLayout(data.layout_style as LayoutStyle);
      } catch { /* use default */ }
    };
    loadLayout();
    const sub = supabase.channel('collection-settings-home')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'collection_settings' }, () => loadLayout())
      .subscribe();
    return () => { supabase.removeChannel(sub); };
  }, []);

  // Load hero banners from Supabase
  useEffect(() => {
    const loadHeroBanners = async () => {
      try {
        const { data, error } = await supabase
          .from('hero_banners')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });
        if (!error && data && data.length > 0) {
          const mapped: Product[] = data.map((b: Record<string, unknown>) => ({
            id: b.id as string,
            name: b.product_name as string,
            price: b.price as number,
            originalPrice: (b.original_price as number) || (b.price as number),
            image: b.image_url as string,
            category: b.category as string,
            badge: b.badge as string,
          }));
          setHeroBanners(mapped);
        }
      } catch { /* use fallback */ }
    };
    loadHeroBanners();
    const sub = supabase.channel('hero-banners-home')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'hero_banners' }, () => loadHeroBanners())
      .subscribe();
    return () => { supabase.removeChannel(sub); };
  }, []);

  const loadTrending = useCallback(() => {
    try {
      const allRaw = localStorage.getItem('listed_products');
      if (allRaw) {
        const all = JSON.parse(allRaw);
        const found = all.filter((p: TrendingProduct & { isTrending?: boolean }) => p.isTrending === true);
        if (found.length > 0) {
          const withImages: TrendingProduct[] = found.map((p: TrendingProduct & { isTrending?: boolean }) => ({
            id: p.id, name: p.name, category: p.category, price: p.price,
            originalPrice: p.originalPrice, sizes: p.sizes ?? [],
            thumbnail: p.thumbnail || (p.images?.[0]) || '',
            images: p.images || [],
          }));
          setTrendingProducts(withImages);
          return;
        }
      }
      const raw = localStorage.getItem('trending_products');
      if (raw) {
        const arr: TrendingProduct[] = JSON.parse(raw);
        setTrendingProducts(arr.length > 0 ? arr : []);
      } else {
        setTrendingProducts([]);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    setIsVisible(true);
    loadTrending();
    const syncInterval = setInterval(loadTrending, 3000);
    return () => clearInterval(syncInterval);
  }, [loadTrending]);

  // Calculate total banners (minimum 1 to avoid modulo by zero)
  const totalBanners = Math.max(1, 4 + trendingProducts.length);

  // Auto-rotate banner timer
  useEffect(() => {
    // Clear any existing timer
    if (bannerTimerRef.current) {
      clearInterval(bannerTimerRef.current);
      bannerTimerRef.current = null;
    }
    
    // Only start timer if we have more than 1 banner
    if (totalBanners > 1) {
      console.log('Starting banner timer, total banners:', totalBanners);
      bannerTimerRef.current = setInterval(() => {
        setActiveBanner(prev => {
          const next = (prev + 1) % totalBanners;
          console.log('Banner changing:', prev, '->', next);
          return next;
        });
      }, 5000);
    }
    
    return () => { 
      if (bannerTimerRef.current) {
        clearInterval(bannerTimerRef.current);
        bannerTimerRef.current = null;
      }
    };
  }, [totalBanners]);

  const goToBanner = (idx: number) => {
    console.log('Manual banner change to:', idx);
    setActiveBanner(idx);
    // Reset timer
    if (bannerTimerRef.current) {
      clearInterval(bannerTimerRef.current);
      bannerTimerRef.current = null;
    }
    if (totalBanners > 1) {
      bannerTimerRef.current = setInterval(() => {
        setActiveBanner(prev => (prev + 1) % totalBanners);
      }, 5000);
    }
  };

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return { hours: 23, minutes: 59, seconds: 59 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCategoryChange = (index: number) => {
    if (index === activeCategory) return;
    setAnimating(true);
    setTimeout(() => { setActiveCategory(index); setAnimating(false); }, 180);
  };

  const getCategoryColors = (index: number) => {
    const baseColors = [
      { from: colors.primary, to: colors.secondary, light: `${colors.primary}15` },
      { from: colors.secondary, to: colors.accent, light: `${colors.secondary}15` },
      { from: colors.accent, to: colors.primary, light: `${colors.accent}15` },
      { from: colors.primary, to: colors.secondary, light: `${colors.primary}15` },
      { from: colors.secondary, to: colors.accent, light: `${colors.secondary}15` },
    ];
    return baseColors[index % baseColors.length];
  };

  const products = heroBanners.filter(p => p.category === categories[activeCategory]);

  const addToCart = (product: Product) => { onAddToCart({ ...product }); };

  // Modals
  const DeliveryModal = () => (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowDeliveryModal(false)}>
      <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="relative p-6 pb-5" style={{ background: 'linear-gradient(135deg, #c2410c, #dc2626)' }}>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
          <button type="button" onClick={() => setShowDeliveryModal(false)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/25 hover:bg-white/40 flex items-center justify-center text-white cursor-pointer transition-all z-20" aria-label="Close modal">
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
              <i className="ri-flashlight-fill text-yellow-300 text-2xl"></i>
            </div>
            <h3 className="text-white font-bold text-xl">Dehradun Delivery</h3>
            <p className="text-white/80 text-sm">Lightning fast — 30 minutes!</p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-gray-700 text-sm leading-relaxed">
            Ab Dehradun mein aapke favourite fashion products sirf <strong className="text-orange-600">30 minutes</strong> mein aapke door par!
          </p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: 'ri-map-pin-2-fill', title: '10km Radius', color: 'text-red-500', bg: 'bg-red-50' },
              { icon: 'ri-e-bike-2-fill', title: 'Live Tracking', color: 'text-orange-500', bg: 'bg-orange-50' },
              { icon: 'ri-shield-check-fill', title: 'COD Only', color: 'text-green-600', bg: 'bg-green-50' },
            ].map(item => (
              <div key={item.title} className={`${item.bg} rounded-2xl p-3 text-center`}>
                <i className={`${item.icon} ${item.color} text-2xl mb-1`}></i>
                <p className="text-gray-800 font-semibold text-xs">{item.title}</p>
              </div>
            ))}
          </div>
          <button onClick={() => setShowDeliveryModal(false)} className="w-full py-3 rounded-2xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold text-sm cursor-pointer hover:opacity-90 transition-all">
            Got it! ✓
          </button>
        </div>
      </div>
    </div>
  );

  const SizeModal = () => (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowSizeModal(false)}>
      <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="relative p-6 pb-5" style={{ background: 'linear-gradient(135deg, #065f46, #047857)' }}>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
          <button type="button" onClick={() => setShowSizeModal(false)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/25 hover:bg-white/40 flex items-center justify-center text-white cursor-pointer transition-all z-20" aria-label="Close modal">
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
              <i className="ri-ruler-line text-emerald-200 text-2xl"></i>
            </div>
            <h3 className="text-white font-bold text-xl">Size Guarantee</h3>
            <p className="text-white/80 text-sm">Wrong size? Zero tension!</p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-gray-700 text-sm leading-relaxed">
            Aap koi bhi size order karo, hamara delivery partner <strong className="text-emerald-600">S, M, L, XL, XXL</strong> sab sizes lekar aayega aapke door par.
          </p>
          <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
            {[
              { step: '1', text: 'Order karo koi bhi size', color: 'bg-orange-500' },
              { step: '2', text: 'Delivery boy sab sizes lekar aayega', color: 'bg-emerald-500' },
              { step: '3', text: 'Try karo, jo fit ho woh lo', color: 'bg-emerald-500' },
              { step: '4', text: 'Baki sizes wapas — Zero extra cost', color: 'bg-emerald-600' },
            ].map(item => (
              <div key={item.step} className="flex items-center gap-3">
                <span className={`w-6 h-6 rounded-full ${item.color} text-white text-xs font-bold flex items-center justify-center shrink-0`}>{item.step}</span>
                <span className="text-gray-700 text-sm">{item.text}</span>
              </div>
            ))}
          </div>
          <button onClick={() => setShowSizeModal(false)} className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-sm cursor-pointer hover:opacity-90 transition-all">
            Bahut Badhiya! ✓
          </button>
        </div>
      </div>
    </div>
  );

  // Common props for all banner components
  const bannerProps = {
    onShopNowClick,
    onAddToCart,
    heroBanners,
    trendingProducts,
    activeBanner,
    goToBanner,
    countdown,
    onDeliveryClick: () => setShowDeliveryModal(true),
    onSizeClick: () => setShowSizeModal(true),
    cart: [] as CartItem[],
    onOpenCart: () => {},
  };

  const renderBanner = () => {
    switch (bannerLayout) {
      case 'card-3d': return <Card3DBanner {...bannerProps} />;
      case 'glass-hero': return <GlassBanner {...bannerProps} />;
      case 'neon-glow': return <NeonGlowBanner {...bannerProps} />;
      case 'big-split-3d': return <BigSplit3DBanner {...bannerProps} />;
      case 'magazine-grid': return <MagazineGridBanner {...bannerProps} />;
      case 'cinematic': return <CinematicBanner {...bannerProps} />;
      case 'theme-showcase': return <ThemeShowcaseBanner {...bannerProps} />;
      case 'announcement': return <AnnouncementBanner {...bannerProps} />;
      case 'minimal-luxury': return <MinimalLuxuryBanner {...bannerProps} />;
      case 'carousel':
      default: return <CarouselBanner {...bannerProps} />;
    }
  };

  return (
    <section id="home" className="relative pt-16 md:pt-[68px] overflow-hidden" style={{ backgroundColor: colors.bg }}>
      {showDeliveryModal && <DeliveryModal />}
      {showSizeModal && <SizeModal />}

      {/* Marquee animation style */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
          display: inline-block;
          white-space: nowrap;
        }
      `}</style>

      <div className={`w-full px-4 sm:px-5 lg:px-8 xl:px-12 pt-5 pb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div className="max-w-6xl mx-auto space-y-5">

          {/* ── DYNAMIC BANNER LAYOUT ── */}
          {renderBanner()}

          {/* ── SEARCH BAR ── */}
          <HomeSearchBar />

          {/* ── FEATURED COLLECTION ── */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-0.5">Featured</p>
                <h2 className="text-gray-900 font-black text-xl">Our Collection</h2>
              </div>
              <Link to={`/category?cat=${encodeURIComponent(categories[activeCategory])}`} className="flex items-center gap-1 text-sm font-bold text-orange-600 hover:text-orange-700 transition-colors">
                View All <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {/* Category Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1 mb-4" style={{ scrollbarWidth: 'none' }}>
              {categories.map((cat, idx) => {
                const c = getCategoryColors(idx);
                return (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(idx)}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-200 cursor-pointer shrink-0"
                    style={activeCategory === idx
                      ? { background: `linear-gradient(135deg, ${c.from}, ${c.to})`, color: '#fff', transform: 'scale(1.04)' }
                      : { background: '#f3f4f6', color: '#4b5563' }
                    }
                  >
                    <i className={`${categoryIcons[cat]} text-sm`}></i>
                    {cat}
                  </button>
                );
              })}
            </div>

            {/* Products Grid */}
            <CollectionGrid
              products={products}
              layout={collectionLayout}
              animating={animating}
              onAddToCart={addToCart}
            />

            {/* Trust Strip */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[
                { icon: 'ri-money-rupee-circle-fill', label: 'COD', sub: 'Pay at door', color: 'text-amber-500', bg: 'bg-amber-50' },
                { icon: 'ri-ruler-line', label: 'Size Promise', sub: 'Try all sizes', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                { icon: 'ri-star-fill', label: '4.8 Stars', sub: '1200+ reviews', color: 'text-yellow-500', bg: 'bg-yellow-50' },
              ].map(b => (
                <div key={b.label} className={`${b.bg} rounded-xl p-3 text-center`}>
                  <i className={`${b.icon} ${b.color} text-2xl`}></i>
                  <p className="text-gray-900 font-bold text-xs mt-1">{b.label}</p>
                  <p className="text-gray-500 text-[10px]">{b.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={onShopNowClick}
              className="flex-1 py-4 rounded-2xl font-black text-sm text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02] cursor-pointer whitespace-nowrap"
              style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
            >
              <i className="ri-flashlight-fill text-base" style={{ color: colors.accent }}></i>
              Shop Now
            </button>
            <Link
              to={`/category?cat=${encodeURIComponent(categories[activeCategory])}`}
              className="flex-1 py-4 rounded-2xl font-black text-sm border-2 border-gray-200 text-gray-800 flex items-center justify-center gap-2 hover:bg-gray-50 hover:scale-[1.02] transition-all whitespace-nowrap"
            >
              <i className="ri-gallery-view-2 text-base"></i>
              View Collection
            </Link>
          </div>

        </div>
      </div>
    </section>
  );
}
